import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import {
	Add,
	Close,
	CommentOutlined,
	EditOutlined,
	ImageOutlined,
	Mood,
	MoodBad,
	SentimentSatisfied,
} from '@material-ui/icons'

import { Button, Checkbox, H3, TextEditor, TOAST_TYPE } from '@base'
import { Button as ButtonV2, Card as CardV2, CardHeaderRow, CardHeaderTitle } from '@baseV2'
import { strings } from '@constants'
import { useDispatch, useSelector } from 'react-redux'
import { uploadCranCommentDocument } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleFileViewer } from '@data/state/action/root'
import BasicModal from '@base/basic/BasicModal'
import { toDocumentUrls } from '../../../cranCommentImages'
import { COLORS, fontFamily, Spacings, Text } from '@styles'



// Sentiment values map to what the API stores: negative -> false, neutral -> null, positive -> true.
const SENTIMENTS = [
	{ value: 0, icon: MoodBad, tone: 'negative' },
	{ value: 1, icon: SentimentSatisfied, tone: 'neutral' },
	{ value: 2, icon: Mood, tone: 'positive' },
]

const toCommentType = (value) => (value === 0 ? false : value === 2 ? true : null)
const toSentiment = (commentType) => (commentType === null || commentType === undefined ? 1 : commentType === true ? 2 : 0)

const StyledCard = styled(CardV2)`
	gap: ${Spacings.SPACING_2B};

	/* The card's own gap already spaces the header; drop the shared header's extra spacing. */
	${CardHeaderRow} {
		padding-bottom: 0;
		margin-bottom: 0;
	}
`

// The header holds the Edit/Add action plus the image upload beside it.
const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

const ImageStrip = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
`

const ImageThumb = styled.div`
	position: relative;
	width: calc(4 * ${Spacings.SPACING_4B});
	height: calc(4 * ${Spacings.SPACING_4B});
	border: 1px solid ${COLORS.gray[200]};
	border-radius: ${({ theme }) => theme.radius.md};
	overflow: hidden;
	cursor: pointer;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`

const RemoveImageButton = styled.button`
	position: absolute;
	top: ${Spacings.SPACING_1};
	right: ${Spacings.SPACING_1};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: ${({ theme }) => theme.radius.full};
	background: rgb(0 0 0 / 55%);
	color: ${({ theme }) => theme.palette.text.white};
	cursor: pointer;
	padding: 0;

	& > svg {
		font-size: ${Text.SMALL};
	}
`

// The comment sits in its own bordered box inside the card, like the address cards do.
const InnerContainer = styled.div`
	border: 1px solid ${COLORS.gray[200]};
	border-radius: ${({ theme }) => theme.radius.md};
	padding: ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	line-height: ${Text.LINE_HEIGHT_MAX};
	font-family: ${fontFamily.inter};
	color: ${({ theme }) => theme.palette.text.black};
	white-space: pre-wrap;
	word-break: break-word;
`



const FooterRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
`

const CheckBoxWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	cursor: pointer;
`

const CheckBoxLabel = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.palette.text.label};
`

const SentimentRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

const SentimentButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: none;
	padding: 0;
	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	color: ${({ theme }) => theme.palette.text.greydark};

	& > svg {
		font-size: ${Text.X_LARGE};
	}

	${({ selected, tone }) => {
		if (!selected) return
		if (tone === 'negative') {
			return css`
				color: ${COLORS.red[500]};
			`
		}
		if (tone === 'neutral') {
			return css`
				color: ${COLORS.yellow[500]};
			`
		}
		return css`
			color: ${COLORS.green[500]};
		`
	}}
`

const EditorWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

const EditorActions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_4B};
`

const BUTTON_OPTIONS = [
	['undo', 'redo'],
	['font', 'fontSize', 'formatBlock'],
	['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
	['fontColor', 'hiliteColor'],
	['align', 'list', 'lineHeight'],
	['outdent', 'indent'],
	['table', 'horizontalRule', 'link'],
	['fullScreen', 'showBlocks', 'codeView'],
]

// Server paths come back with either slash ('cran/X.jpg' or 'cran\\X.jpg'), so documents are
// matched on the file name alone.
const fileNameOf = (path) => String(path ?? '').replace(/\\/g, '/').split('/').pop().toLowerCase()

const toPlainText = (html) => {
	const div = document.createElement('div')
	div.innerHTML = html
	return div.textContent || div.innerText || ''
}

const CranCommentCard = ({
	label,
	savedDocuments,
	textValue,
	toggleState = null,
	onToggleChange,
	checkValue,
	onCheckHandler,
	onButtonClick,
	disabled,
	probeId,
	commentKey,
}) => {
	const dispatch = useDispatch()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [open, setOpen] = useState(false)
	// The editor works on a draft so the card behind it only changes once the comment is saved;
	// cancelling throws the draft away.
	const [draft, setDraft] = useState('')

	const selectedSentiment = useMemo(() => toSentiment(toggleState), [toggleState])
	const plainText = useMemo(() => toPlainText(textValue || ''), [textValue])
	const hasComment = !!plainText.trim()

	// Images attached to this comment. Each pick is uploaded straight away, one request per file.
	const [images, setImages] = useState([])
	const fileInputRef = useRef(null)

	// Documents already uploaded against this comment, as the report API returns them. They are read
	// only here — the upload endpoint has no delete counterpart — and sit before this session's picks.
	const savedImages = useMemo(
		() => (savedDocuments ?? []).map((path, index) => ({ id: `${path}-${index}`, url: toDocumentUrls([path])[0], path, saved: true })),
		[savedDocuments]
	)

	// A refetch (comment saved, sentiment clicked, drawer reopened) brings this session's uploads back
	// as saved documents, so the local previews are dropped — otherwise the same image shows twice.
	const savedKey = savedImages.map((item) => item.path).join('|')
	useEffect(() => {
		setImages((prev) => {
			if (!prev.length) return prev
			prev.forEach((item) => URL.revokeObjectURL(item.url))
			return []
		})
	}, [savedKey])

	const allImages = useMemo(() => {
		const savedNames = new Set(savedImages.map((item) => fileNameOf(item.path)))
		return [...savedImages, ...images.filter((item) => !item.path || !savedNames.has(fileNameOf(item.path)))]
	}, [savedImages, images])

	const openEditor = () => {
		setDraft(textValue || '')
		setOpen(true)
	}

	const pickImages = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
			fileInputRef.current.click()
		}
	}

	const imagesChangeHandler = async (e) => {
		const picked = Array.from(e?.target?.files ?? [])
		if (!picked.length) return

		dispatch(showLoader(true))
		const uploaded = []
		const failed = []

		for (const file of picked) {
			try {
				// eslint-disable-next-line no-await-in-loop -- the endpoint takes one file per call
				const response = await uploadCranCommentDocument({ file, probe42Id: probeId, userId: loggedInUser?.user_id, commentKey })
				const path = typeof response === 'string' ? response : response?.file_path ?? response?.path ?? response?.file_paths?.[0]
				uploaded.push({ id: `${file.name}-${file.lastModified}-${file.size}`, name: file.name, path, url: URL.createObjectURL(file) })
			} catch (error) {
				failed.push({ name: file.name, message: error?.response?.data?.message ?? error?.message })
			}
		}

		if (uploaded.length) {
			setImages((prev) => [...prev, ...uploaded.filter((item) => !prev.some((existing) => existing.id === item.id))])
			dispatch(showToast(true, strings('msg_document_uploaded'), { type: TOAST_TYPE.SUCCESS }))
		}
		if (failed.length) {
			const reason = failed[0]?.message
			const names = failed.map((item) => item.name).join(', ')
			dispatch(
				showToast(true, `${strings('document_upload_failed')} ${names}${reason ? ` — ${reason}` : ''}`, { type: TOAST_TYPE.ERROR })
			)
		}
		dispatch(showLoader(false))
	}

	// Removing only drops it from this card — the upload endpoint has no delete counterpart.
	const removeImage = (id) => (e) => {
		e.stopPropagation()
		setImages((prev) => {
			const removed = prev.find((item) => item.id === id)
			if (removed) URL.revokeObjectURL(removed.url)
			return prev.filter((item) => item.id !== id)
		})
	}

	const viewImages = () => () => {
		dispatch(toggleFileViewer(true, { files: allImages.map((item) => item.url) }))
	}

	const saveHandler = () => {
		onButtonClick(draft)
		setOpen(false)
	}

	return (
		<StyledCard hoverable={false}>
			{/* Header mirrors the address cards: icon + title on the left, the action link on the right.
			    Without a comment the action is a solid Add button instead of the Edit link. */}
			<CardHeaderRow>
				<CardHeaderTitle>
					<CommentOutlined />
					{label}
				</CardHeaderTitle>
				<HeaderActions>
					{hasComment ? (
						<ButtonV2 variant='link' color='primary' onClick={openEditor} disabled={disabled}>
							<EditOutlined />
							{strings('edit')}
						</ButtonV2>
					) : (
						<ButtonV2 variant='solid' color='primary' onClick={openEditor} disabled={disabled}>
							<Add fontSize='small' />
							{strings('add')}
						</ButtonV2>
					)}
					{/* Images belong to a comment, so there is nothing to attach them to until one exists. */}
					{hasComment && (
						<ButtonV2 variant='link' color='primary' onClick={pickImages} disabled={disabled}>
							<ImageOutlined />
							{strings('upload')}
						</ButtonV2>
					)}
				</HeaderActions>
			</CardHeaderRow>

			<input type='file' multiple accept='image/*' ref={fileInputRef} style={{ display: 'none' }} onChange={imagesChangeHandler} />

			{allImages.length > 0 && (
				<ImageStrip>
					{allImages.map((item, index) => (
						<ImageThumb key={item.id} onClick={viewImages(index)}>
							<img src={item.url} alt='' />
							{/* Only this session's picks can be taken back — saved ones have no delete endpoint. */}
							{!item.saved && (
								<RemoveImageButton type='button' onClick={removeImage(item.id)} title={strings('delete')}>
									<Close />
								</RemoveImageButton>
							)}
						</ImageThumb>
					))}
				</ImageStrip>
			)}

			{hasComment && (
				<>
					<InnerContainer>{plainText}</InnerContainer>

					<FooterRow>
						<CheckBoxWrapper>
							<Checkbox
								size={1.2}
								disabled={disabled}
								checked={checkValue}
								value={checkValue}
								onChange={(e) => onCheckHandler(e.target.checked)}
							/>
							<CheckBoxLabel>{'Show in CRAN'}</CheckBoxLabel>
						</CheckBoxWrapper>
						<SentimentRow>
							{SENTIMENTS.map(({ value, icon: Icon, tone }) => (
								<SentimentButton
									key={value}
									type='button'
									tone={tone}
									selected={selectedSentiment === value}
									disabled={disabled}
									onClick={() => onToggleChange(toCommentType(value))}
								>
									<Icon />
								</SentimentButton>
							))}
						</SentimentRow>
					</FooterRow>
				</>
			)}

			<BasicModal isOpen={open} onClose={() => setOpen(false)} closeIcon={false}>
				<EditorWrapper>
					<H3>{label}</H3>
					<TextEditor height={'40vh'} closeIcon={false} value={draft} onChange={setDraft} ButtonOptions={BUTTON_OPTIONS} />
					<EditorActions>
						<Button xs type='secondary' variant='destructive' onClick={() => setOpen(false)}>
							{strings('cancel')}
						</Button>
						<Button xs type='primary' onClick={saveHandler}>
							{strings('save')}
						</Button>
					</EditorActions>
				</EditorWrapper>
			</BasicModal>
		</StyledCard>
	)
}

export default CranCommentCard
